import {
  Alert,
  Button,
  ComponentState,
  Divider,
  Flex,
  Group,
  Icon,
  ModifiedDrawer,
  Recording,
  Tag,
  Text,
  Title,
} from "components/common"
import { useLazyGetReviewDetailQuery } from "features/clients/detail/api"
import { t } from "i18next"
import { forwardRef, useImperativeHandle, useRef, useState } from "react"
import { convertToReadableString, localisedValue, openBase64 } from "utils"
import theme from "utils/themeVars"
import styles from "./index.module.less"
import cx from "clsx"
import { ratingColorMap } from "features/shared/data"
import { ReviewReportSkeleton } from "features/leads/components/addReviewLead/utils"
import { useQueryUpdate } from "hooks"

const RATING_QUESTION_KEYS = ["overall_rating", "call_rating", "task_rating"]
const MAX_VISIBLE_MULTI_TAGS = 4

const ReviewDetailDrawer = forwardRef(({ client }, ref) => {
  const drawerRef = useRef()
  const [reviewId, setReviewId] = useState(null)
  const [expandedTagsByField, setExpandedTagsByField] = useState({})
  const [getReviewDetail, { data, isFetching, error }] = useLazyGetReviewDetailQuery()
  const reviewData = data?.data
  const [update] = useQueryUpdate()

  useImperativeHandle(
    ref,
    () => ({
      showFor: id => {
        setReviewId(id)
        setExpandedTagsByField({})
        drawerRef?.current?.setVisible(true)
      },
    }),
    []
  )

  const onDrawerVisible = () => {
    if (!reviewId) return
    getReviewDetail(reviewId)
  }

  const closeDrawer = () => {
    drawerRef?.current?.setVisible(false)
    update("getReviewDetail", draft => null)
    setReviewId(null)
    setExpandedTagsByField({})
  }

  const expandMultiTags = fieldKey => {
    setExpandedTagsByField(prev => ({ ...prev, [fieldKey]: true }))
  }

  const renderAnswer = (item, currentReviewData, fieldKey) => {
    const value = item?.displayValue || "-"

    if (item?.fieldType === "attachment") {
      let attachments = []
      try {
        const parsedValue = typeof item?.value === "string" ? JSON.parse(item?.value || "[]") : item?.value
        attachments = Array.isArray(parsedValue) ? parsedValue : []
      } catch {
        attachments = []
      }
      if (!attachments?.length) return <Text className="fs14">-</Text>
      return (
        <Flex gap="8px" wrap="wrap">
          {attachments?.map((file, index) => (
            <a
              className={styles.attachmentChip}
              href={file?.path || file?.file}
              onClick={event => {
                const filePreview = file?.file
                event.preventDefault()
                openBase64(filePreview)
              }}
              rel="noreferrer"
              target="_blank"
            >
              <Icon icon="BsFileEarmarkText" color={theme["primary-color"]} size="14px" />
              <Text className="fs14">{file?.name || t("Attachment")}</Text>
            </a>
          ))}
        </Flex>
      )
    }

    if (item?.fieldType === "multi_select") {
      const values = item?.displayTags || []
      if (!values?.length > 0) return <Text className="gray-800 fs14">-</Text>
      const shouldCollapse = values.length > MAX_VISIBLE_MULTI_TAGS
      const isExpanded = !!expandedTagsByField?.[fieldKey]
      const remainingCount = values.length - MAX_VISIBLE_MULTI_TAGS
      const visibleValues = shouldCollapse && !isExpanded ? values.slice(0, MAX_VISIBLE_MULTI_TAGS) : values

      return (
        <Flex gap="6px" wrap="wrap">
          {visibleValues.map((entry, index) => (
            <Tag key={`${entry}-${index}`} className={cx(styles.multiTag, "fs14")}>
              {entry}
            </Tag>
          ))}
          {shouldCollapse && !isExpanded && (
            <Tag
              type="light"
              color={theme["accent-color"]}
              className={cx(styles.multiTag, "fs14 pointer")}
              onClick={() => expandMultiTags(fieldKey)}
            >
              +{remainingCount}
            </Tag>
          )}
        </Flex>
      )
    }

    if (item?.fieldType === "text") {
      return (
        <div className={styles.textBox}>
          <Text className="fs14 gray-900">{value || "-"}</Text>
          {item?.questionKey === "remarks" && !!currentReviewData?.voiceNote?.path && (
            <>
              <div className="mbe-8" />
              <div className={styles.audioPlayerShell}>
                <div className={styles.audioWrap}>
                  <Recording
                    className="w-100"
                    id={currentReviewData?.voiceNote?.id}
                    recordingSrc={currentReviewData?.voiceNote?.path}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      )
    }

    if (item?.fieldType === "scale") {
      const scaleEntries = item?.displayScaleEntries || []
      if (!scaleEntries?.length > 0) return <Text className="gray-800 fs14">-</Text>

      return (
        <div className={styles.scaleBox}>
          <Flex vertical gap="2px" align="stretch">
            {scaleEntries.map((entry, index) => (
              <Flex key={`${entry?.label}-${index}`} justify="space-between" align="center" className="pb-8">
                <Flex align="center" gap="8px">
                  <Icon icon="GoDotFill" color={theme["primary-color"]} size="15px" />
                  <Text className="gray-900 fs14">{entry?.label}</Text>
                </Flex>
                <Text className="gray-900 fs14">{entry?.score ?? ""}</Text>
              </Flex>
            ))}
          </Flex>
        </div>
      )
    }

    const displayValue = value || "-"

    if (RATING_QUESTION_KEYS.includes(item?.questionKey)) {
      const ratingColor = ratingColorMap()[displayValue?.trim?.() || displayValue]

      if (ratingColor) {
        return (
          <Tag type="light" color={ratingColor} pill className={styles.ratingTag}>
            {displayValue}
          </Tag>
        )
      }
    }

    return <Text className="fs14">{displayValue}</Text>
  }

  const renderContent = () => {
    if (isFetching && !reviewData) {
      return <ReviewReportSkeleton />
    }

    if (error) {
      return (
        <ComponentState
          description={error?.message}
          status={error?.status}
          onClick={() => {
            if (reviewId) getReviewDetail(reviewId)
          }}
        />
      )
    }

    if (!reviewData) return <Text className="gray-800 fs14">-</Text>

    const hasRemarksQuestion = Object.values(reviewData?.sections ?? {}).some(sectionQuestions =>
      (sectionQuestions ?? [])?.some(question => question?.questionKey === "remarks")
    )

    return (
      <Flex vertical gap="20px" align="stretch" className="pbe-16">
        {Object.entries(reviewData?.sections ?? {}).map(([sectionKey, questions]) => {
          const validity = reviewData?.entityValidityInfo
          const showValidityBanner = validity?.sectionKey === sectionKey

          return (
            <div key={sectionKey}>
              <Title level={3} className="mbe-0">
                {t(convertToReadableString(sectionKey, true))}
              </Title>
              <Divider style={{ marginBlock: "10px 16px" }} />

              {showValidityBanner && (
                <Alert
                  type={validity?.isValid ? "success" : "error"}
                  color={"red"}
                  showIcon
                  className={cx(styles.validityAlert, "mbe-24 w-100")}
                  message={t("The {{entityType}} was marked {{status}}", {
                    entityType: t(validity?.entityType),
                    status: validity?.isValid ? t("valid") : t("invalid"),
                  })}
                  style={{ "--ant-color-success-bg": "#e6f5e7" }}
                />
              )}

              <Flex vertical gap="14px" align="stretch">
                {(questions ?? [])?.map((question, index) => (
                  <Group
                    key={`${question?.questionId || question?.questionKey}-${index}`}
                    template="220px 1fr"
                    gap="16px"
                    style={{ alignItems: "start" }}
                  >
                    <Text className="gray-800 fs14">
                      {localisedValue({ object: question, key: "questionTitle" }) || "-"}
                    </Text>
                    <div>
                      {renderAnswer(
                        question,
                        reviewData,
                        `${sectionKey}-${question?.questionId || question?.questionKey || index}`
                      )}
                    </div>
                  </Group>
                ))}
              </Flex>
            </div>
          )
        })}

        {!!reviewData?.voiceNote?.path && !hasRemarksQuestion && (
          <Group template="220px 1fr" gap="16px" style={{ alignItems: "start", width: "100%" }}>
            <Text className="gray-800 fs14">{t("Voice Note")}</Text>
            <div className={styles.audioPlayerShell}>
              <div className={styles.audioWrap}>
                <Recording
                  className="w-100"
                  id={reviewData?.voiceNote?.id}
                  recordingSrc={reviewData?.voiceNote?.path}
                />
              </div>
            </div>
          </Group>
        )}
      </Flex>
    )
  }

  return (
    <>
      <ModifiedDrawer
        ref={drawerRef}
        placement="right"
        width={1100}
        title={t("Review Details")}
        onClose={closeDrawer}
        onDrawerVisible={onDrawerVisible}
        destroyOnClose
        footer={
          <Flex justify="end">
            <Button text={t("Close")} onClick={closeDrawer} />
          </Flex>
        }
      >
        {renderContent()}
      </ModifiedDrawer>
    </>
  )
})

export default ReviewDetailDrawer
